import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTaxYear } from "../contexts/TaxYearContext";
import {
  LayoutDashboard, Upload, Receipt, FileText, DollarSign, TrendingDown,
  BookOpen, FileBarChart2, Download, StickyNote, Settings, HelpCircle,
  LogOut, ChevronDown, AlertTriangle, ShieldCheck, TrendingUp, ChevronLeft, ChevronRight,
  Crown, Files, Sparkles, Users, Award, Package, Calendar, Lock, FolderArchive
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import DisclaimerModal from "./DisclaimerModal";
import OnboardingWizard from "./OnboardingWizard";
import MigrationPickerModal from "./MigrationPickerModal";
import RenewalWelcomeModal from "./RenewalWelcomeModal";
import RenewalGraceBanner from "./RenewalGraceBanner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const navItems = [
  { path: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/app/upload", icon: Upload, label: "Upload & Extract" },
  { path: "/app/vault", icon: FolderArchive, label: "On-The-Go Vault" },
  { path: "/app/transactions", icon: Receipt, label: "Transactions" },
  { path: "/app/cacfp", icon: FileBarChart2, label: "CACFP Overview" },
  { path: "/app/grant-tracking", icon: Award, label: "Grant Tracking" },
  { path: "/app/equipment", icon: Package, label: "Equipment & Assets" },
  { path: "/app/income", icon: DollarSign, label: "Income" },
  { path: "/app/expenses", icon: TrendingDown, label: "Expenses" },
  { path: "/app/reports", icon: BookOpen, label: "Reports & Documents" },
  { path: "/app/archive", icon: Package, label: "Archive" },
  { path: "/app/year-end", icon: Lock, label: "Year-End" },
  { path: "/app/turbotax", icon: FileText, label: "Tax Prep Document" },
  { path: "/app/notes", icon: StickyNote, label: "Notes & Reminders" },
  { path: "/app/settings", icon: Settings, label: "Settings" },
  { path: "/app/help", icon: HelpCircle, label: "Help Center" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { taxYear } = useTaxYear();
  const navigate = useNavigate();
  const location = useLocation();
  const [centerOpen, setCenterOpen] = useState(false);
  const [progress, setProgress] = useState({ approved: 0, total: 0, pct: 0 });
  const [usage, setUsage] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sst_sidebar_collapsed") === "1"; } catch { return false; }
  });
  // Sidebar badge — count of Equipment & Assets drafts that need user review.
  // Updates via custom "equipment:changed" window event (dispatched after Confirm /
  // Discard / Add Equipment) and on route changes.
  const [equipDraftCount, setEquipDraftCount] = useState(0);
  const [needsCatCount, setNeedsCatCount] = useState(0);

  const isOwner = user?.role === "owner";

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const nv = !c;
      try { localStorage.setItem("sst_sidebar_collapsed", nv ? "1" : "0"); } catch {/*ignore*/}
      return nv;
    });
  };

  const license = user?.license || {};
  const isTrialExpiring = license.status === "trial" && license.days_remaining <= 3;
  const isExpired = license.status === "expired";

  // Fetch Tax Prep Progress (approved / total) on mount + every page change + every 15s
  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        const { data } = await axios.get(`${API}/transactions/summary`, { withCredentials: true });
        if (cancelled) return;
        const total = data.total_transactions || 0;
        const approved = data.approved_count || 0;
        setProgress({
          approved,
          total,
          pct: total > 0 ? Math.round((approved / total) * 100) : 0,
        });
      } catch {/* silent */}
    };
    const fetchUsage = async () => {
      try {
        const { data } = await axios.get(`${API}/usage`, { params: { tax_year: taxYear }, withCredentials: true });
        if (!cancelled) setUsage(data);
      } catch {/* silent */}
    };
    fetchProgress();
    fetchUsage();
    const t = setInterval(() => { fetchProgress(); fetchUsage(); }, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]);

  // Equipment drafts badge — re-fetches on mount, route change, and on global
  // "equipment:changed" event dispatched by EquipmentAssets after writes.
  useEffect(() => {
    let cancelled = false;
    const fetchDrafts = async () => {
      try {
        const { data } = await axios.get(`${API}/equipment`, { withCredentials: true });
        if (!cancelled) setEquipDraftCount(data.needs_review_count || 0);
      } catch {/* silent — badge just stays at last known value */}
    };
    fetchDrafts();
    const onChanged = () => fetchDrafts();
    window.addEventListener("equipment:changed", onChanged);
    return () => { cancelled = true; window.removeEventListener("equipment:changed", onChanged); };
  }, [location.pathname]);

  // Item 2 of pre-3d roadmap — Transactions "Needs Category" sidebar badge.
  // Mirrors the Equipment Drafts badge pattern.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await axios.get(`${API}/transactions/needs-categorization-count`, { withCredentials: true });
        if (!cancelled) setNeedsCatCount(data.count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const onChanged = (e) => {
      if (e?.detail && typeof e.detail.count === "number") setNeedsCatCount(e.detail.count);
      else fetchCount();
    };
    window.addEventListener("transactions:categorization:changed", onChanged);
    return () => { cancelled = true; window.removeEventListener("transactions:categorization:changed", onChanged); };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-16" : "w-64"} flex-shrink-0 flex flex-col z-20 transition-[width] duration-200`}
        style={{ background: "linear-gradient(180deg, #6D28D9 0%, #2563EB 100%)" }}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <p className="text-white font-bold text-sm leading-tight" style={{ fontFamily: "Manrope" }}>Simple Sign In Tax</p>
                <p className="text-white/60 text-xs">for Childcare Centers</p>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5 thin-scroll-dark">
          {navItems.map(({ path, icon: Icon, label }) => {
            const active = location.pathname === path;
            const isEquipment = path === "/app/equipment";
            const isTransactions = path === "/app/transactions";
            const showBadge = isEquipment && equipDraftCount > 0;
            const showTxnBadge = isTransactions && needsCatCount > 0;
            const badgeLabel = equipDraftCount > 99 ? "99+" : String(equipDraftCount);
            const txnBadgeLabel = needsCatCount > 99 ? "99+" : String(needsCatCount);
            return (
              <Link
                key={path}
                to={path}
                title={collapsed ? (showBadge ? `${label} — ${badgeLabel} draft${equipDraftCount === 1 ? "" : "s"} need review` : label) : undefined}
                data-testid={`sidebar-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 rounded-lg text-sm transition-all duration-200 group relative
                  ${active
                    ? collapsed
                      ? "bg-white/20 text-white font-semibold"
                      : "bg-white/20 text-white border-l-4 border-white pl-2.5 font-semibold"
                    : "text-white/75 hover:bg-white/10 hover:text-white hover:translate-x-0.5"
                  }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-white" : "text-white/70 group-hover:text-white"}`} />
                {!collapsed && <span className="truncate flex-1">{label}</span>}
                {/* Equipment Drafts badge */}
                {showBadge && !collapsed && (
                  <span
                    data-testid="sidebar-equipment-drafts-badge"
                    className="inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-400 text-amber-900 min-w-[20px] h-[20px] px-1.5 leading-none flex-shrink-0"
                    title={`${badgeLabel} draft${equipDraftCount === 1 ? "" : "s"} need review`}
                  >
                    {badgeLabel}
                  </span>
                )}
                {showBadge && collapsed && (
                  <span
                    data-testid="sidebar-equipment-drafts-dot"
                    className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-purple-700"
                    aria-label={`${badgeLabel} drafts need review`}
                  />
                )}
                {/* Transactions "Needs Category" badge */}
                {showTxnBadge && !collapsed && (
                  <span
                    data-testid="sidebar-needs-cat-badge"
                    className="inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-400 text-amber-900 min-w-[20px] h-[20px] px-1.5 leading-none flex-shrink-0"
                    title={`${txnBadgeLabel} transaction${needsCatCount === 1 ? "" : "s"} need a category`}
                  >
                    {txnBadgeLabel}
                  </span>
                )}
                {showTxnBadge && collapsed && (
                  <span
                    data-testid="sidebar-needs-cat-dot"
                    className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-purple-700"
                    aria-label={`${txnBadgeLabel} transactions need a category`}
                  />
                )}
              </Link>
            );
          })}
          {/* Stage E — Upgrade Plan link (shown to all accounts, incl. owner, for display parity) */}
          {(
            <Link
              to="/app/pricing"
              title={collapsed ? "Upgrade Plan" : undefined}
              data-testid="sidebar-nav-pricing"
              className={`flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                location.pathname === "/app/pricing"
                  ? collapsed ? "bg-white/20 text-white font-semibold" : "bg-white/20 text-white border-l-4 border-white pl-2.5 font-semibold"
                  : "text-emerald-200/90 hover:bg-emerald-400/15 hover:text-emerald-100"
              }`}
            >
              <Sparkles className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">Upgrade Plan</span>}
            </Link>
          )}
          {/* Owner-only — Owner Console link */}
          {isOwner && (
            <Link
              to="/app/owner"
              title={collapsed ? "Owner Console" : undefined}
              data-testid="sidebar-nav-owner-console"
              className={`flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 mt-2 rounded-lg text-sm transition-all duration-200 group border-t border-white/10 pt-3 ${
                location.pathname === "/app/owner"
                  ? collapsed ? "bg-yellow-400/20 text-yellow-100 font-semibold" : "bg-yellow-400/20 text-yellow-100 border-l-4 border-yellow-300 pl-2.5 font-semibold"
                  : "text-yellow-200/80 hover:bg-yellow-400/10 hover:text-yellow-100"
              }`}
            >
              <Crown className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">Owner Console</span>}
            </Link>
          )}
          {/* Owner-only — Customer Portal link */}
          {isOwner && (
            <Link
              to="/app/customer-portal"
              title={collapsed ? "Customer Portal" : undefined}
              data-testid="sidebar-nav-customer-portal"
              className={`flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                location.pathname === "/app/customer-portal"
                  ? collapsed ? "bg-yellow-400/20 text-yellow-100 font-semibold" : "bg-yellow-400/20 text-yellow-100 border-l-4 border-yellow-300 pl-2.5 font-semibold"
                  : "text-yellow-200/80 hover:bg-yellow-400/10 hover:text-yellow-100"
              }`}
            >
              <Users className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">Customer Portal</span>}
            </Link>
          )}
        </nav>

        {/* License Banner — only when expanded */}
        {!collapsed && (isTrialExpiring || isExpired) && (
          <div className={`mx-3 mb-2 p-3 rounded-lg ${isExpired ? "bg-red-500/20 border border-red-300/30" : "bg-amber-500/20 border border-amber-300/30"}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-xs font-semibold">{isExpired ? "Trial Expired" : `${license.days_remaining} day${license.days_remaining !== 1 ? "s" : ""} left`}</p>
                <p className="text-white/70 text-xs mt-0.5">From $99/year — choose your plan</p>
                <Link to="/app/pricing" className="text-yellow-300 text-xs font-semibold hover:underline">Upgrade Now →</Link>
              </div>
            </div>
          </div>
        )}
        {!collapsed && license.status === "trial" && !isTrialExpiring && (
          <div className="mx-3 mb-2 p-2.5 rounded-lg bg-white/10 border border-white/10">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-green-300" />
              <div>
                <p className="text-white/80 text-xs">{license.days_remaining} days left in trial</p>
                <Link to="/app/pricing" className="text-white/60 text-xs hover:text-white">View Plans →</Link>
              </div>
            </div>
          </div>
        )}
        {!collapsed && license.type === "signature" && (
          <div className="mx-3 mb-2 p-3 rounded-lg bg-gradient-to-r from-yellow-500/20 to-amber-400/20 border border-yellow-300/30">
            <div className="flex items-center gap-2">
              <span className="text-lg">👑</span>
              <div>
                <p className="text-yellow-200 text-xs font-bold">Signature Account</p>
                <p className="text-yellow-300/70 text-xs">Lifetime Access</p>
              </div>
            </div>
          </div>
        )}
        {!collapsed && license.status === "active" && license.type === "paid" && (
          <div className="mx-3 mb-2 p-2.5 rounded-lg bg-green-500/20 border border-green-300/20">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-green-300" />
              <p className="text-white/80 text-xs">
                {license.license_expires_at
                  ? `Licensed through ${new Date(license.license_expires_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
                  : "Licensed"}
              </p>
            </div>
          </div>
        )}

        {/* Page Usage chip — non-owner customers only (owners have unlimited) */}
        {!collapsed && usage && usage.status !== "unlimited" && (
          <Link
            to="/app/settings"
            data-testid="sidebar-page-usage-chip"
            title={`Pages: ${usage.pages_used_year} of ${usage.pages_quota_year} used`}
            className="mx-3 mb-2 p-2.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 transition-all block"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/70 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Files className="w-3 h-3" /> Pages
              </span>
              <span className="text-white text-[11px] font-bold">{usage.percent_used}%</span>
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  usage.status === "maxed_out" ? "bg-red-400" :
                  usage.status === "critical" ? "bg-orange-400" :
                  usage.status === "warning" ? "bg-amber-300" :
                  "bg-emerald-400"
                }`}
                style={{ width: `${Math.min(usage.percent_used, 100)}%` }}
              />
            </div>
            <p className="text-white/60 text-[10px] mt-1">
              {usage.pages_used_year} / {usage.pages_quota_year}
              {usage.pages_purchased_extra > 0 && ` (+${usage.pages_purchased_extra} extra)`}
            </p>
          </Link>
        )}

        {/* Tax Prep Progress (constant — visible on every page; condensed when collapsed) */}
        <div className={`relative group ${collapsed ? "mx-2 mb-2" : "mx-3 mb-3"}`}>
          <button
            onClick={() => navigate("/app/transactions")}
            data-testid="sidebar-tax-prep-progress"
            title={collapsed ? `Tax Prep Progress: ${progress.pct}%` : undefined}
            className={`w-full ${collapsed ? "p-2 flex items-center justify-center" : "p-3 text-left"} rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 transition-all`}
          >
            {!collapsed && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/70 text-[10px] font-bold uppercase tracking-wider">Tax Prep Progress</span>
                <TrendingUp className="w-3.5 h-3.5 text-white/60 group-hover:text-white" />
              </div>
            )}
            <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
              {/* Ring */}
              <div className={`relative ${collapsed ? "w-9 h-9" : "w-12 h-12"} flex-shrink-0`}>
                <svg className={`${collapsed ? "w-9 h-9" : "w-12 h-12"} -rotate-90`} viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15" fill="none"
                    stroke={progress.pct >= 67 ? "#34D399" : progress.pct >= 33 ? "#FBBF24" : "#F87171"}
                    strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${(progress.pct / 100) * 94.25} 94.25`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-white ${collapsed ? "text-[9px]" : "text-[11px]"} font-bold`} style={{ fontFamily: "Manrope" }}>{progress.pct}%</span>
                </div>
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold leading-tight">
                    {progress.approved} <span className="text-white/60 font-normal">/ {progress.total}</span>
                  </p>
                  <p className="text-white/60 text-[10px] mt-0.5">
                    {progress.total === 0 ? "No items yet" :
                      progress.total - progress.approved === 0 ? "✓ All approved" :
                      `${progress.total - progress.approved} to review`}
                  </p>
                </div>
              )}
            </div>
          </button>

          {/* Hover tooltip — always available, fuller info */}
          <div
            data-testid="sidebar-tax-prep-tooltip"
            className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 w-64 z-50
                       opacity-0 group-hover:opacity-100 transition-opacity duration-200
                       bg-slate-900 text-white text-xs rounded-lg shadow-xl border border-slate-700 p-3"
          >
            <p className="font-bold text-sm mb-1.5" style={{ fontFamily: "Manrope" }}>Tax Prep Progress</p>
            <p className="text-slate-200 leading-relaxed mb-1.5">
              Approved line items ÷ total extracted items.
            </p>
            <p className="text-slate-300 text-[11px] mb-2">
              Example: 14 of 33 = 42%
            </p>
            <p className="text-slate-300 text-[11px] italic border-t border-slate-700 pt-2">
              Approve items in Transactions to raise your score.
            </p>
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-y-[6px] border-y-transparent border-r-[6px] border-r-slate-900" />
          </div>
        </div>

        {/* User info + Sign Out */}
        <div className={`${collapsed ? "px-2" : "px-3"} pb-2 border-t border-white/10 pt-3`}>
          <button
            onClick={() => setCenterOpen(!centerOpen)}
            title={collapsed ? user?.center_name || "My Center" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-3 px-3"} py-2 rounded-lg hover:bg-white/10 transition-all`}
            data-testid="sidebar-user-menu"
          >
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{user?.name?.[0] || "U"}</span>
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-white text-xs font-semibold truncate">{user?.center_name || "My Center"}</p>
                  <p className="text-white/60 text-xs truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-white/60" />
              </>
            )}
          </button>
          <button
            onClick={handleLogout}
            title={collapsed ? "Sign Out" : undefined}
            data-testid="sidebar-sign-out"
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-3 px-3"} py-2 mt-1 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all text-sm`}
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>

        {/* Collapse toggle — bottom of sidebar */}
        <button
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-toggle"
          title={collapsed ? "Expand" : "Collapse"}
          className={`w-full flex items-center ${collapsed ? "justify-center" : "justify-center gap-1.5"} py-2.5 border-t border-white/10 text-white/70 hover:bg-white/10 hover:text-white transition-all text-xs font-medium`}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>}
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-slate-50 thin-scroll">
        <TopBar />
        <RenewalGraceBanner />
        {isExpired && !isOwner && <TrialExpiredBanner />}
        <Outlet />
      </main>

      {/* Global one-time modals (Feb 2026) */}
      <DisclaimerModal />
      <OnboardingWizard />
      <MigrationPickerModal />
      <RenewalWelcomeModal />
    </div>
  );
}

// ── Trial Expired Banner — Sprint 2 Batch 3 ─────────────────────────────────
// Persistent, non-dismissible banner shown on every authenticated page when
// a non-owner user's free trial has expired. Links directly to /app/pricing.
function TrialExpiredBanner() {
  return (
    <div
      data-testid="trial-expired-banner"
      className="w-full bg-red-600 text-white flex items-center justify-between px-5 py-2.5 text-sm font-medium z-20"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>Your free trial has ended. Upgrade now to continue accessing your tax data.</span>
      </div>
      <Link
        to="/app/pricing"
        data-testid="trial-expired-banner-cta"
        className="ml-4 flex-shrink-0 bg-white text-red-600 font-bold text-xs px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
      >
        Upgrade Now
      </Link>
    </div>
  );
}

// ── Top Bar — TurboTax-style Tax Year selector (Ship Gate 2b) ───────────────
// Pages that filter by Tax Year (Dashboard / Income / Expenses / Tax Prep /
// Equipment / Grant Tracking / Reports) hide this badge isn't relevant on the
// CACFP page (CACFP has its own per-page FY picker). Bar appears on every
// authenticated page so users always know which year they're viewing.
function TopBar() {
  const navigate = useNavigate();
  const { taxYear, setTaxYear, availableYears, getYearStatus, currentCalendarYear } = useTaxYear();
  const location = useLocation();
  // Hide the Tax Year selector on pages where it doesn't apply.
  const HIDDEN_PATHS = ["/app/settings", "/app/help", "/app/cacfp", "/app/notes", "/app/customer-portal", "/app/owner"];
  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;
  const isPast = taxYear < currentCalendarYear;
  const selectedStatus = getYearStatus(taxYear);
  // "Locked" = backend says nothing to view yet (inactive/unprovisioned) —
  // read_only/archived years ARE viewable, just not writable.
  const showActivatePrompt = selectedStatus.can_view === false;
  return (
    <div
      data-testid="tax-year-topbar"
      className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-3 backdrop-blur"
    >
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-slate-400" />
        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Tax Year</span>
      </div>
      <select
        data-testid="tax-year-selector"
        value={taxYear}
        onChange={(e) => setTaxYear(e.target.value)}
        className="text-sm font-extrabold text-slate-900 border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
        style={{ fontFamily: "Manrope" }}
      >
        {availableYears.map((y) => {
          const status = getYearStatus(y);
          let suffix = y === currentCalendarYear ? "  • current" : "";
          if (status.entitlement_state === "read_only") suffix += "  (Read-only)";
          else if (status.entitlement_state === "archived") suffix += "  (Archived)";
          else if (status.can_view === false) suffix += "  (Locked)";
          const label = `${y} (Jan – Dec)${suffix}`;
          return (
            <option key={y} value={y} disabled={status.can_view === false}>{label}</option>
          );
        })}
      </select>
      {showActivatePrompt && (
        <button
          type="button"
          data-testid="tax-year-activate-prompt"
          onClick={() => navigate(`/app/year-end/activate?tax_year=${taxYear}`)}
          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded transition-colors"
          title={`Tax Year ${taxYear} has not been activated yet.`}
        >
          <Lock className="w-3 h-3" /> Locked — Activate
        </button>
      )}
      {isPast && !showActivatePrompt && (
        <span
          data-testid="tax-year-past-badge"
          className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded"
          title="You're viewing a past tax year. Switch to current to edit."
        >
          Past year — read-only after subscription end
        </span>
      )}
      <span className="text-[11px] text-slate-400 ml-auto leading-tight italic">
        IRS Schedule C is filed by calendar year. Switch above to view another year's data.
      </span>
    </div>
  );
}
